import { useEffect, useState } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import { getInventory } from '../api';

// StocksManagement doc §21/§23 - read-only "Inventory" visibility for an owner/admin editing an
// existing listing: how many units this listing represents (one row per hospitality room type
// where applicable, otherwise one overall) and the InventoryTransaction audit trail behind every
// commit/release/block. Blocked date ranges are edited separately via <BlockedDatesFields> (its
// own dedicated route/save button) - this panel only reports what the ledger recorded as a
// result, it is not another place to edit anything.
//
// Renders nothing until there's a real itemId - a listing that doesn't exist yet has no ledger.

const TYPE_LABEL = {
  OPENING_STOCK: 'Opening stock', PURCHASE: 'Purchase', BOOKING: 'Booking', CANCELLATION: 'Cancellation',
  RETURN: 'Return', DAMAGE: 'Damage', LOSS: 'Loss', REPAIR: 'Repair',
  BLOCK: 'Blocked', UNBLOCK: 'Unblocked', MANUAL_ADJUSTMENT: 'Manual adjustment'
};

export default function InventoryPanel({ itemId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!itemId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    getInventory(itemId)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Could not load inventory activity.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [itemId]);

  if (!itemId) return null;
  if (loading) return <div className="my-2"><Spinner animation="border" size="sm" role="status" /></div>;
  if (error) return <div className="small text-danger my-2">{error}</div>;
  if (!data) return null;

  const transactions = showAll ? data.transactions : data.transactions.slice(0, 8);

  return (
    <div className="border rounded p-2 mb-3 bg-light bg-opacity-50">
      <div className="fw-semibold small mb-2">Inventory</div>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {data.resources.map((r) => (
          <Badge key={r._id} bg="secondary" className="fw-normal">
            {r.name || 'This listing'}: {r.totalQuantity} unit{r.totalQuantity === 1 ? '' : 's'} ({r.availabilityGranularity.toLowerCase()})
          </Badge>
        ))}
      </div>
      {transactions.length === 0 ? (
        <div className="small text-muted">No inventory activity yet.</div>
      ) : (
        <>
          <div className="small text-muted mb-1">Recent activity</div>
          <ul className="list-unstyled small mb-1" style={{ maxHeight: 220, overflowY: 'auto' }}>
            {transactions.map((t) => (
              <li key={t._id} className="d-flex justify-content-between border-bottom py-1">
                <span>
                  {TYPE_LABEL[t.type] || t.type}
                  {t.resourceName ? <span className="text-muted"> — {t.resourceName}</span> : null}
                  {t.note ? <span className="text-muted"> ({t.note})</span> : null}
                </span>
                <span className="text-nowrap ms-2">
                  <span className={t.quantityDelta < 0 ? 'text-danger' : 'text-success'}>
                    {t.quantityDelta > 0 ? '+' : ''}{t.quantityDelta}
                  </span>
                  <span className="text-muted ms-2">
                    {new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          {data.transactions.length > 8 && (
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowAll((s) => !s)}>
              {showAll ? 'Show less' : `Show all ${data.transactions.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
